/* eslint-disable indent */
/* eslint-disable no-console */
/* eslint-disable unicorn/prefer-module */
import React, { useEffect, useState, useMemo } from "react";
import { Dropdown } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import Modal from "react-modal";
import { EDIFICIOS_EXCLUIDOS } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import { getEdificios } from "../../utils";
import { getLocale } from "../../utils/getLocale";
import FichaEdificioWindow from "../FichaEdificioWindow";
import { Header } from "../Header";
import { Loader } from "../Loader";
import styles from "./InventarioAvanzado.module.scss";

Modal.setAppElement("#root");

export const InventarioAvanzado = () => {
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortedColumn, setSortedColumn] = useState(null);
  const [selectedEdificioId, setSelectedEdificioId] = useState(null);
  const [allEdificios, setAllEdificios] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState([]);
  const [filtroUbicacion, setFiltroUbicacion] = useState([]);
  const [filtroCircuito, setFiltroCircuito] = useState([]);
  const [estadosUnicos, setEstadosUnicos] = useState([]);
  const [showEstadoFilter, setShowEstadoFilter] = useState(true);

  const { pais } = useAuthContext();

  useEffect(() => {
    const initialize = async () => {
      const allBuildings = await getEdificios({ pais });
      setAllEdificios(allBuildings);
    };

    initialize();
  }, [pais]);

  const handleSort = (column) => {
    if (sortedColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortedColumn(column);
      setSortOrder("asc");
    }
  };

  const handleEdificioClick = (id) => {
    setSelectedEdificioId(id);
  };

  useEffect(() => {
    if (allEdificios.length > 0) {
      const uniqueEstados = [...new Set(allEdificios.map((edificio) => edificio.estado))].filter(
        (estado) => estado && estado !== "PROYECTO" && estado !== "STANDBY"
      );
      setEstadosUnicos(uniqueEstados);
      setShowEstadoFilter(uniqueEstados.length > 1);
    }
  }, [allEdificios]);

  const edificios = useMemo(() => {
    const ESTADOS_PERMITIDOS = ["OPERATIVO", "INSTALACIÓN", "REACTIVACIÓN"];

    return allEdificios.filter((edificio) => {
      // 0. Excluir edificios no comercializables
      if (EDIFICIOS_EXCLUIDOS.includes(edificio.id)) return false;

      // 1. Filtro por estado seleccionado
      if (filtroEstado.length > 0 && !filtroEstado.includes(edificio.estado)) return false;

      // 2. Filtro por ubicación
      if (filtroUbicacion.length > 0 && !filtroUbicacion.includes(edificio.ubicacion)) return false;

      // 3. Filtro por circuito (usando ?? 0 para evitar null)
      if (filtroCircuito.length > 0) {
        const hasAnyCircuito = filtroCircuito.some((circuito) => {
          switch (circuito) {
            case "pantallas":
              return (edificio.pantallas ?? 0) > 0;
            case "circulación":
              return (edificio.totems ?? 0) + (edificio.cowork ?? 0) > 0;
            case "espera":
              return (edificio.espera ?? 0) > 0;
            case "cowork":
              return (edificio.cowork ?? 0) > 1;
            default:
              return false;
          }
        });
        if (!hasAnyCircuito) return false;
      }

      // 4. Solo incluir si está en los estados permitidos
      return ESTADOS_PERMITIDOS.includes(edificio.estado);
    });
  }, [allEdificios, filtroEstado, filtroUbicacion, filtroCircuito]);

  const sortedEdificios = useMemo(() => {
    if (!edificios) return [];
    const estadoPrioridad = {
      OPERATIVO: 1,
      INSTALACIÓN: 2,
      REACTIVACIÓN: 3,
      STANDBY: 4,
      PRÓXIMO: 5,
    };
    return [...edificios].sort((a, b) => {
      if (sortedColumn === "estado") {
        if (estadoPrioridad[a.estado] < estadoPrioridad[b.estado]) return sortOrder === "asc" ? -1 : 1;
        if (estadoPrioridad[a.estado] > estadoPrioridad[b.estado]) return sortOrder === "asc" ? 1 : -1;
      } else if (sortedColumn === "ubicacion") {
        return sortOrder === "asc" ? a.ubicacion.localeCompare(b.ubicacion) : b.ubicacion.localeCompare(a.ubicacion);
      } else if (sortedColumn === "personas") {
        return sortOrder === "asc" ? a.personas - b.personas : b.personas - a.personas;
      } else if (sortedColumn === "edificio") {
        return sortOrder === "asc" ? a.edificio.localeCompare(b.edificio) : b.edificio.localeCompare(a.edificio);
      } else if (sortedColumn === "estacionamientos") {
        return sortOrder === "asc"
          ? (a.estacionamientos || 0) - (b.estacionamientos || 0)
          : (b.estacionamientos || 0) - (a.estacionamientos || 0);
      } else if (sortedColumn === "empresas") {
        return sortOrder === "asc"
          ? calculateEmpresasCantidad(a.empresaslistado) - calculateEmpresasCantidad(b.empresaslistado)
          : calculateEmpresasCantidad(b.empresaslistado) - calculateEmpresasCantidad(a.empresaslistado);
      } else if (sortedColumn === "is_programmatic") {
        return sortOrder === "asc"
          ? (a.is_programmatic === true ? 1 : -1) - (b.is_programmatic === true ? 1 : -1)
          : (b.is_programmatic === true ? 1 : -1) - (a.is_programmatic === true ? 1 : -1);
      } else {
        if (estadoPrioridad[a.estado] < estadoPrioridad[b.estado]) return -1;
        if (estadoPrioridad[a.estado] > estadoPrioridad[b.estado]) return 1;
        if (a.personas !== b.personas) {
          return b.personas - a.personas;
        }
        return a.edificio.localeCompare(b.edificio);
      }
    });
  }, [edificios, sortedColumn, sortOrder]);

  const calculateEmpresasCantidad = (empresaslistado) => {
    if (!empresaslistado || empresaslistado.toUpperCase() === "NO DISPONIBLE") {
      return 0;
    }
    return empresaslistado.split(",").length;
  };

  const displayEmpresasCantidad = (empresaslistado) => {
    const cantidad = calculateEmpresasCantidad(empresaslistado);
    return cantidad === 0 ? "-" : cantidad; // Muestra "-" solo si cantidad es 0
  };

  const totalEmpresasCantidad = useMemo(
    () =>
      sortedEdificios.reduce((total, edificio) => {
        const cantidad = calculateEmpresasCantidad(edificio.empresaslistado);
        return total + cantidad; // Suma todas las cantidades, incluyendo 0, 1, 2, etc.
      }, 0),
    [sortedEdificios]
  );

  let a = 1;

  const renderUbicacionOptions = () => {
    if (!allEdificios) return null;

    const ordenEstados = ["OPERATIVO", "INSTALACIÓN", "STANDBY", "PRÓXIMO"];
    const ubicacionesConEstados = {};

    allEdificios.forEach((edificio) => {
      if (!ubicacionesConEstados[edificio.ubicacion]) {
        ubicacionesConEstados[edificio.ubicacion] = [];
      }
      ubicacionesConEstados[edificio.ubicacion].push(edificio.estado);
    });

    const ubicacionesOrdenadas = Object.keys(ubicacionesConEstados).sort((a, b) => {
      const estadoA = Math.min(...ubicacionesConEstados[a].map((estado) => ordenEstados.indexOf(estado)));
      const estadoB = Math.min(...ubicacionesConEstados[b].map((estado) => ordenEstados.indexOf(estado)));
      return estadoA - estadoB;
    });

    return ubicacionesOrdenadas.map((ubicacion, index) => (
      <div key={index} className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`ubicacion-${index}`}
          value={ubicacion}
          checked={filtroUbicacion.includes(ubicacion)}
          onChange={() => {
            if (filtroUbicacion.includes(ubicacion)) {
              setFiltroUbicacion(filtroUbicacion.filter((u) => u !== ubicacion));
            } else {
              setFiltroUbicacion([...filtroUbicacion, ubicacion]);
            }
          }}
          style={{ marginLeft: "-40px" }}
        />
        <label className="form-check-label" htmlFor={`ubicacion-${index}`} style={{ marginLeft: "-10px" }}>
          {ubicacion}
        </label>
      </div>
    ));
  };

  const renderCircuitoOptions = () =>
    ["pantallas", "circulación", "espera", "cowork"].map((circuito, index) => (
      <div key={index} className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`circuito-${index}`}
          value={circuito}
          checked={filtroCircuito.includes(circuito)}
          onChange={() => {
            if (filtroCircuito.includes(circuito)) {
              setFiltroCircuito(filtroCircuito.filter((c) => c !== circuito));
            } else {
              setFiltroCircuito([...filtroCircuito, circuito]);
            }
          }}
          style={{ marginLeft: "-40px" }}
        />
        <label className="form-check-label" htmlFor={`circuito-${index}`} style={{ marginLeft: "-10px" }}>
          {circuito === "pantallas" ? "Ascensores" : circuito.charAt(0).toUpperCase() + circuito.slice(1)}
        </label>
      </div>
    ));

  const totalEdificios = sortedEdificios.length;
  const totalOperativos = sortedEdificios.filter((e) => e.estado === "OPERATIVO").length;
  const totalInstalacion = sortedEdificios.filter((e) => e.estado === "INSTALACIÓN").length;
  const totalPantallas = sortedEdificios.reduce(
    (sum, e) => sum + (e.pantallas || 0) + (e.totems || 0) + (e.espera || 0) + (e.cowork || 0),
    0
  );
  const totalAudiencia = sortedEdificios.reduce((sum, e) => sum + (e.personas || 0), 0);

  return (
    <div className={styles.inventarioAvanzadoTable}>
      <Header
        leftContent={
          <div className={styles.statsContainer}>
            <div className={styles.statLine}>
              Edificios: <strong>{totalEdificios}</strong>
            </div>
            <div className={styles.statLine}>
              Audiencia:{" "}
              <strong>
                {totalAudiencia.toLocaleString(getLocale(pais), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>{" "}
              personas únicas
            </div>
            <div className={styles.statLine}>
              Pantallas: <strong>{totalPantallas}</strong>
            </div>
            <div className={styles.statLine}>
              Alcance B2B:{" "}
              <strong>
                {totalEmpresasCantidad.toLocaleString(getLocale(pais), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>{" "}
              empresas
            </div>
          </div>
        }
      >
        <div className={styles.mobileStats}>
          <div className={styles.mobileStatItem}>
            Edificios: <strong>{totalEdificios}</strong>
          </div>
          <div className={styles.mobileStatItem}>
            Pantallas: <strong>{totalPantallas}</strong>
          </div>
          <div className={styles.mobileStatItem}>
            Personas:{" "}
            <strong>
              {totalAudiencia.toLocaleString(getLocale(pais), {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </strong>
          </div>
        </div>
        <div className={styles.filterRow}>
          {showEstadoFilter && (
            <div className={styles.filterItem}>
              <select
                className={styles.filterSelect}
                value={filtroEstado[0] || ""}
                onChange={(e) => setFiltroEstado(e.target.value ? [e.target.value] : [])}
              >
                <option value="">Status</option>
                {estadosUnicos.map((estado, i) => (
                  <option key={i} value={estado}>
                    {estado}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.filterItem}>
            <Dropdown>
              <Dropdown.Toggle
                variant="white"
                id="dropdown-ubicacion"
                className={`${styles.filterSelect} ${filtroUbicacion.length > 0 ? styles.activeFilter : ""}`}
              >
                {filtroUbicacion.length > 0 ? filtroUbicacion.join(", ") : "Ubicación"}
              </Dropdown.Toggle>
              <Dropdown.Menu>{renderUbicacionOptions()}</Dropdown.Menu>
            </Dropdown>
          </div>

          <div className={styles.filterItem}>
            <Dropdown>
              <Dropdown.Toggle
                variant="white"
                id="dropdown-circuito"
                className={`${styles.filterSelect} ${filtroCircuito.length > 0 ? styles.activeFilter : ""}`}
              >
                {filtroCircuito.length > 0
                  ? filtroCircuito
                      .map((c) => (c === "pantallas" ? "Ascensores" : c.charAt(0).toUpperCase() + c.slice(1)))
                      .join(", ")
                  : "Circuito"}
              </Dropdown.Toggle>
              <Dropdown.Menu>{renderCircuitoOptions()}</Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Header>
      {allEdificios.length > 0 ? (
        <div className={styles.tableContainer}>
          <table className={`${styles.table} table table-striped table-hover table-bordered table-sm`}>
            <thead className="text-center align-middle">
              <tr>
                <th rowSpan="2" className={`${styles.col3} ${styles.alwaysVisible}`}>
                  #
                </th>
                <th
                  className={`${styles.col15} ${sortedColumn === "edificio" ? "sorted" : ""}`}
                  onClick={() => handleSort("edificio")}
                  rowSpan="2"
                >
                  EDIFICIO{sortedColumn === "edificio" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col10} ${sortedColumn === "estado" ? "sorted" : ""}`}
                  onClick={() => handleSort("estado")}
                  rowSpan="2"
                >
                  STATUS{sortedColumn === "estado" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col12} ${sortedColumn === "ubicacion" ? "sorted" : ""}`}
                  onClick={() => handleSort("ubicacion")}
                  rowSpan="2"
                >
                  UBICACIÓN{sortedColumn === "ubicacion" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col22} ${sortedColumn === "direccion" ? "sorted" : ""}`}
                  onClick={() => handleSort("direccion")}
                  rowSpan="2"
                >
                  DIRECCIÓN
                </th>
                <th
                  className={`${styles.col10} ${sortedColumn === "is_programmatic" ? "sorted" : ""}`}
                  onClick={() => handleSort("is_programmatic")}
                  rowSpan="2"
                >
                  PROGRAMMATIC{sortedColumn === "is_programmatic" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col8} ${sortedColumn === "personas" ? "sorted" : ""}`}
                  onClick={() => handleSort("personas")}
                  rowSpan="2"
                >
                  PERSONAS DIARIAS {sortedColumn === "personas" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col8} ${sortedColumn === "empresas" ? "sorted" : ""}`}
                  onClick={() => handleSort("empresas")}
                  rowSpan="2"
                >
                  EMPRESAS{sortedColumn === "empresas" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col8} ${sortedColumn === "estacionamientos" ? "sorted" : ""}`}
                  onClick={() => handleSort("estacionamientos")}
                  rowSpan="2"
                >
                  PARKINGS{sortedColumn === "estacionamientos" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col20} ${sortedColumn === "pantallas" ? "sorted" : ""}`}
                  onClick={() => handleSort("pantallas")}
                  colSpan="4"
                >
                  PANTALLAS
                </th>
              </tr>
              <tr className="table-active">
                <th className={styles.col5}>ASC.</th>
                <th className={styles.col5}>CIR.</th>
                <th className={styles.col5}>ESP.</th>
                <th className={styles.col5}>
                  TOTAL{sortedColumn === "espera" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
              </tr>
            </thead>
            <tbody className={styles.tbody}>
              {sortedEdificios.map(
                ({
                  id,
                  edificio,
                  pantallas,
                  ubicacion,
                  direccion,
                  personas,
                  estado,
                  is_programmatic,
                  totems,
                  espera,
                  cowork,
                  estacionamientos,
                  empresaslistado,
                }) => {
                  const className =
                    estado === "OPERATIVO"
                      ? "estadoOperativo"
                      : estado === "PRÓXIMO"
                        ? "estadoProximo"
                        : "estadoStandby";
                  const statusStyle = {
                    backgroundColor:
                      estado === "OPERATIVO"
                        ? "green"
                        : estado === "REACTIVACIÓN"
                          ? "dodgerblue"
                          : estado === "INSTALACIÓN"
                            ? "orange"
                            : estado === "STANDBY"
                              ? "red"
                              : "darkorange",
                    color: "white",
                  };
                  return (
                    <tr key={id}>
                      <td className={styles.col3}>{a++}</td>
                      <td
                        className={`${styles.col15} ${styles.clickable} text-start`}
                        onClick={() => handleEdificioClick(id)}
                      >
                        {edificio.toUpperCase()}
                      </td>
                      <td className={`col-8 ${className}`} style={statusStyle}>
                        {estado}
                      </td>
                      <td>{ubicacion}</td>
                      <td className="text-start">{direccion}</td>
                      <td>{is_programmatic === true ? <FaCheck /> : "-"}</td>
                      <td>
                        {(personas ?? 0).toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>{displayEmpresasCantidad(empresaslistado)}</td>
                      <td>{estacionamientos ? estacionamientos : "-"}</td>
                      <td>{pantallas ? pantallas : "-"}</td>
                      <td>{totems ? totems : cowork ? cowork : "-"}</td>
                      <td>{espera ? espera : "-"}</td>
                      <td>{pantallas + totems + cowork + espera}</td>
                    </tr>
                  );
                }
              )}
            </tbody>
            <tfoot className={styles.bottom}>
              <tr className={styles.bottomRow}>
                <td colSpan="6" />
                <td className={styles.col8}>
                  {sortedEdificios
                    .map((item) => item.personas)
                    .reduce((prev, curr) => prev + curr, 0)
                    .toLocaleString(getLocale(pais), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </td>
                <td className={styles.col8}>{totalEmpresasCantidad}</td>
                <td className={styles.col8}>
                  {sortedEdificios
                    .map((item) => item.estacionamientos || 0)
                    .reduce((prev, curr) => prev + curr, 0)
                    .toLocaleString(getLocale(pais), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </td>
                <td className={styles.col5}>
                  {sortedEdificios.map((item) => item.pantallas).reduce((prev, curr) => prev + curr, 0)}
                </td>
                <td className={styles.col5}>
                  {sortedEdificios.map((item) => item.totems + item.cowork).reduce((prev, curr) => prev + curr, 0)}
                </td>
                <td className={styles.col6}>
                  {sortedEdificios.map((item) => item.espera).reduce((prev, curr) => prev + curr, 0)}
                </td>
                <td className={styles.col6}>
                  {sortedEdificios
                    .map((item) => item.pantallas + item.totems + item.cowork + item.espera)
                    .reduce((prev, curr) => prev + curr, 0)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <Loader />
      )}
      {/* Mobile: listado simple */}
      <div className={styles.mobileList}>
        <div className={styles.mobileDetalle}>
          <span className={styles.mobileDetalleItem}>
            <span className={styles.mobileDetalleDot} style={{ backgroundColor: "green" }} />
            {totalOperativos} operativos
          </span>
          {totalInstalacion > 0 && (
            <span className={styles.mobileDetalleItem}>
              <span className={styles.mobileDetalleDot} style={{ backgroundColor: "orange" }} />
              {totalInstalacion} en instalación
            </span>
          )}
        </div>
        {sortedEdificios.map((edificio, idx) => (
          <div key={edificio.id} className={styles.mobileRow}>
            <span className={styles.mobileNum}>{idx + 1}</span>
            <div className={styles.mobileInfo}>
              <span className={styles.mobileName}>{edificio.edificio.toUpperCase()}</span>
              <span className={styles.mobileUbi}>{edificio.ubicacion}</span>
            </div>
            <span className={styles.mobilePantallas}>
              <span className={styles.mobilePantallasLabel}>pantallas</span>
              {(edificio.pantallas || 0) + (edificio.totems || 0) + (edificio.cowork || 0) + (edificio.espera || 0)}
            </span>
          </div>
        ))}
        <div className={styles.mobileNota}>Para más detalle, revisar en versión desktop</div>
      </div>
      {selectedEdificioId && <FichaEdificioWindow id={selectedEdificioId} pais={pais} />}
    </div>
  );
};
